import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AnalyticsListener } from '@atlaskit/analytics-next';
import { normalizeUrl } from '@atlaskit/linking-common/url';
import { ANALYTICS_CHANNEL } from '../constants';
var DEFAULT_CONTEXT_ATTRIBUTES = {
  linkState: 'newLink',
  linkFieldContent: null,
  linkFieldContentInputMethod: null,
  linkFieldContentInputSource: null,
  displayTextFieldContent: null,
  displayTextFieldContentInputMethod: null,
  tab: null
};
var LinkPickerAnalyticsContext = /*#__PURE__*/React.createContext({
  trackAttribute: function trackAttribute() {},
  trackAttributes: function trackAttributes() {},
  getAttributes: function getAttributes() {
    return DEFAULT_CONTEXT_ATTRIBUTES;
  }
});

/**
 * TODO: Should we hook this into the form state/reducer?
 * This function continues to duplicate / assume logic that runs in the picker
 */
var contextAttributesFromIntialProps = function contextAttributesFromIntialProps(props) {
  var _props$plugins$0$tabK, _props$plugins;
  return {
    linkState: normalizeUrl(props.url) ? 'editLink' : 'newLink',
    linkFieldContent: normalizeUrl(props.url) ? 'url' : null,
    displayTextFieldContent: Boolean(props.displayText) ? 'text_string' : null,
    // TODO: Assumes we alway load the first plugin as the current tab
    tab: (_props$plugins$0$tabK = (_props$plugins = props.plugins) === null || _props$plugins === void 0 || (_props$plugins = _props$plugins[0]) === null || _props$plugins === void 0 ? void 0 : _props$plugins.tabKey) !== null && _props$plugins$0$tabK !== void 0 ? _props$plugins$0$tabK : null
  };
};

/**
 * Context for tracking attributes to deliver at a context-level
 */
var LinkPickerAnalytics = function LinkPickerAnalytics(_ref) {
  var initialContext = _ref.initialContext,
    children = _ref.children;
  var dataRef = useRef(_objectSpread(_objectSpread({}, DEFAULT_CONTEXT_ATTRIBUTES), initialContext));
  var methods = useMemo(function () {
    return {
      trackAttribute: function trackAttribute(key, value) {
        dataRef.current[key] = value;
      },
      trackAttributes: function trackAttributes(attributes) {
        dataRef.current = _objectSpread(_objectSpread({}, dataRef.current), attributes);
      },
      getAttributes: function getAttributes() {
        return dataRef.current;
      }
    };
  }, []);
  return /*#__PURE__*/React.createElement(LinkPickerAnalyticsContext.Provider, {
    value: methods
  }, children);
};

/**
 * Hook that exposes the context-level attribute getters and setters.
 */
export var useLinkPickerAnalytics = function useLinkPickerAnalytics() {
  return useContext(LinkPickerAnalyticsContext);
};

/**
 * Wrap component in "attributes" context store and initialise the initial context attributes from props.
 */
function withLinkPickerAnalytics(WrappedComponent) {
  return function (props) {
    var _useState = useState(function () {
        return contextAttributesFromIntialProps(props);
      }),
      _useState2 = _slicedToArray(_useState, 1),
      initialContext = _useState2[0];
    return /*#__PURE__*/React.createElement(LinkPickerAnalytics, _extends({
      initialContext: initialContext
    }, props), /*#__PURE__*/React.createElement(WrappedComponent, props));
  };
}

/**
 * Wraps a component with the analytics context + listener to update events with contextual-level attributes.
 * Should be implemented once at the root of the link picker.
 */
export function withLinkPickerAnalyticsContext(WrappedComponent) {
  return withLinkPickerAnalytics(function (props) {
    var _useLinkPickerAnalyti = useLinkPickerAnalytics(),
      getAttributes = _useLinkPickerAnalyti.getAttributes;
    var onEvent = useCallback(function (event) {
      var _event$payload$attrib;
      event.update({
        attributes: _objectSpread(_objectSpread({}, getAttributes()), (_event$payload$attrib = event.payload.attributes) !== null && _event$payload$attrib !== void 0 ? _event$payload$attrib : {})
      });
    }, [getAttributes]);
    return /*#__PURE__*/React.createElement(AnalyticsListener, {
      channel: ANALYTICS_CHANNEL,
      onEvent: onEvent
    }, /*#__PURE__*/React.createElement(WrappedComponent, props));
  });
}